import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ColorPaletteFromImage = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/color-palette-from-image',
			name: 'Color Palette Generator from Image — Extract Dominant Colors by Lift',
			description:
				'Upload any photo and generate a full color palette from your image — extract dominant tones with exact HEX, RGB, and HSL codes for every color. Try Lift today.',
			url: 'https://liftapp.ai/color-palette-from-image',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/color-palette-from-image#product' },
				{ '@id': 'https://liftapp.ai/color-palette-from-image#howto' },
				{ '@id': 'https://liftapp.ai/color-palette-from-image#benefits' },
				{ '@id': 'https://liftapp.ai/color-palette-from-image#faq' },
				{ '@id': 'https://liftapp.ai/color-palette-from-image#why-lift' },
				{ '@id': 'https://liftapp.ai/color-palette-from-image#explore-tools' },
				{ '@id': 'https://liftapp.ai/color-palette-from-image#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/color-palette-from-image#product',
			name: 'Color Palette Generator from Image',
			description:
				'Upload any photo and generate a full color palette from your image — extract dominant tones with exact HEX, RGB, and HSL codes for every color. Try Lift today.',
			url: 'https://liftapp.ai/color-palette-from-image',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/color-palette-from-image#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Generate your color palette',
					text: 'Lift generates a color palette from your image automatically, extracting dominant tones as clean, copyable swatches.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/color-palette-from-image#benefits',
			name: 'Who can benefit from the Lift Color Palette Generator?',
			description: 'Who can benefit from the Lift Color Palette Generator?',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Brand designers & creative teams — Extract exact colors from campaign imagery to build consistent visual systems. The auto-generated palette delivers a full color breakdown ready to use in any design tool.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Interior designers & home décor enthusiasts — Pull the complete color scheme from any room or mood board photo. Every dominant tone appears as a copyable swatch, from wall colors to accent details.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: "Fashion & lifestyle content creators — Keep posts visually consistent by generating a color palette from your hero shot. Matching content colors to a signature image takes seconds with Lift's analysis.",
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Web & app developers — Derive UI color variables directly from brand photography without switching tools. Lift returns precise HEX and RGB values ready to drop into CSS or a design system.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Photographers & photo editors — Analyze the dominant tones in a scene before retouching to plan your color grade. The auto-palette maps the full color composition of any image in one view.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Lift generates a color palette from a flower bouquet image with burgundy, blush, and sage tones',
				description:
					'Color picker on a ranunculus flower bouquet showing extracted palette with burgundy, pink, sage, and cream tones',
			},
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/color-palette-from-image#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Color Palette Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The Lift Color Palette Generator creates a color palette from any uploaded image by extracting its dominant tones automatically. It also includes a live eyedropper to identify any individual color in the photo and get its exact HEX, RGB, or HSL code.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift Color Palette Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a photo and Lift automatically generates a color palette from your image, extracting dominant tones as clean, copyable swatches. You can also hover the eyedropper over any spot to pick a specific color and copy the code in your preferred format.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I generate a color palette from any type of photo?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Yes — Lift works with any image you upload, whether it's a product shot, landscape, portrait, or screenshot. The palette generator extracts dominant tones from any photo and returns them with HEX, RGB, and HSL codes ready to copy.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I pick multiple colors from the same image?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — you can use the eyedropper as many times as you need on the same photo. Each pick returns the exact HEX, RGB, and HSL values, so you can gather a full set of colors from any image in one session.',
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/color-palette-from-image#why-lift',
			name: 'Why Lift Color Palette from Image',
			description:
				'High-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Lift color palette generator from image — mobile view of ceramic bowls photo with floating color swatches',
				description:
					'Smartphone showing Lift color picker on a ceramic bowls photo with extracted color swatches in HEX format',
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/color-palette-from-image#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/color-palette-from-image#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Color Palette from Image',
					item: 'https://liftapp.ai/color-palette-from-image',
				},
			],
		},
	],
};
